import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteSelectedEvent,
  MatAutocompleteTrigger,
} from '@angular/material/autocomplete';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import {
  ROOMS_REFERENCE_CATEGORIES,
  ROOMS_REFERENCE_CATEGORY_FIELD,
  ROOMS_REFERENCE_SECTION,
  ROOMS_STATUS,
} from '@shared/constants';
import { IReferenceCategoryResponse, IRooms } from '@shared/interfaces';

import { FlowControlService } from '../../../../services/flow-control.service';
import {
  SnackbarService,
  SUCCESS_TYPES,
} from '../../../../services/snackbar.service';
import {
  ReferenceCategoryService,
  ReferenceService,
} from '../../../references/services';
import { RoomsService } from '../../services/rooms.service';

@Component({
  selector: 'app-add-edit-rooms',
  templateUrl: './add-edit-rooms.component.html',
  styleUrls: ['./add-edit-rooms.component.scss'],
})
export class AddEditRoomsComponent implements OnInit {
  @ViewChild('auto', { static: false }) matAutocomplete: MatAutocomplete;
  autoComplete: MatAutocompleteTrigger;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private flowControlService: FlowControlService,
    public translate: TranslateService,
    private snackbar: SnackbarService,
    private roomsService: RoomsService,
    private referenceService: ReferenceService,
    private referenceCategoryService: ReferenceCategoryService
  ) {
    this.flowControlService.updateRouteInfo(
      this.router.url.split('?')[0],
      this.route.snapshot.data
    );
  }
  isLoading = false;
  loadingCount = 0;

  isEdit = false;
  roomId: string;

  addEditRoomsFormGroup: FormGroup<{
    name: FormControl<string>;
    seatingCapacity: FormControl<string>;
    description: FormControl<string>;
    availableResources: FormControl<string[]>;
  }>;

  resources: string[];
  filteredResources: string[] = [];
  selectedResourcesKeyList = [];
  selectable = true;
  removable = true;

  isDisableForm = false;

  referenceCategories: IReferenceCategoryResponse[];

  ngOnInit() {
    this.listenToReferences(); //Get the available resources
    this.editInit();
    this.addEditRoomsFormGroup = new FormGroup({
      name: new FormControl(null, [
        Validators.required,
        this.noWhitespaceValidator,
      ]),
      seatingCapacity: new FormControl(null, [Validators.required]),
      description: new FormControl(null),
      availableResources: new FormControl([]),
    });
  }

  editInit() {
    this.roomId = this.route.snapshot.params.id;
    if (this.roomId) {
      this.isEdit = true;
      this.getRoom();
    }
  }

  noWhitespaceValidator(control: FormControl) {
    const isWhitespace = (control.value || '').trim().length === 0;
    const isValid = !isWhitespace;
    return isValid ? null : { whitespace: true };
  }

  listenToReferences() {
    this.setLoading(true);
    this.referenceCategoryService.allCategories.subscribe(async (data) => {
      if (data && data.length === 0) {
        await this.referenceCategoryService.getAllReferenceCategories();
      }
      if (
        this.referenceCategories?.length === 0 ||
        this.referenceCategories === undefined
      ) {
        this.referenceCategories = data;

        const categoriesId = this.referenceCategories.find((category) => {
          return (
            ROOMS_REFERENCE_SECTION.ROOMS.toUpperCase() ===
              category.section.toUpperCase() &&
            category?.name?.trim()?.toUpperCase() ===
              ROOMS_REFERENCE_CATEGORIES.RESOURCES.toUpperCase()
          );
        })?._id;

        if (categoriesId) {
          const availableResourcesId = this.referenceCategories.find(
            (category) =>
              ROOMS_REFERENCE_SECTION.ROOMS.toUpperCase() ===
                category.section.toUpperCase() &&
              category?.name?.trim()?.toUpperCase() ===
                ROOMS_REFERENCE_CATEGORIES.RESOURCES.toUpperCase()
          )?._id;

          if (availableResourcesId) {
            await this.referenceService
              .getReferences(availableResourcesId?.toString())
              .then((data) => {
                this.resources = [];
                data?.forEach((data) => {
                  data.reference?.forEach((ref) => {
                    if (
                      ref.field.name.toUpperCase() ===
                        ROOMS_REFERENCE_CATEGORY_FIELD.NAME.toUpperCase() &&
                      !data?.is_deleted
                    ) {
                      this.resources.push(data.reference[0].value.toString());
                    }
                  });
                });
                this.setLoading(false);
              });
          } else {
            this.setLoading(false);
          }
        } else {
          this.setLoading(false);
        }
      } else {
        this.setLoading(false);
      }
    });
  }

  getRoom(): void {
    this.setLoading(true);
    this.roomsService.getRoomById(this.roomId).subscribe({
      next: async (res) => {
        const room = res.data;
        this.addEditRoomsFormGroup.patchValue({
          name: room.name,
          description: room.description,
          availableResources: room.available_resources,
          seatingCapacity: room.seating_capacity.toString(),
        });
        this.filteredResources = room.available_resources;
        this.filteredResources.forEach((filteredResource) => {
          const index = this.resources.indexOf(filteredResource);
          if (index !== -1) {
            this.resources.splice(index, 1);
          }
        });

        this.addEditRoomsFormGroup.markAsPristine();
        this.addEditRoomsFormGroup.markAsUntouched();
        this.setLoading(false);
      },
      error: () => {
        this.setLoading(false);
        this.router.navigate(['error']);
      },
    });
  }

  setLoading(isLoading: boolean) {
    if (isLoading) {
      this.loadingCount++;
    } else {
      this.loadingCount--;
    }
    this.isLoading = this.loadingCount > 0;
  }

  remove(fruit: string): void {
    const index = this.filteredResources.indexOf(fruit);

    if (index !== -1) {
      this.filteredResources.splice(index, 1);
      this.resources.push(fruit);
    }
  }

  optionPanelOpen() {
    this.autoComplete.openPanel();
  }

  selected(event: MatAutocompleteSelectedEvent): void {
    this.filteredResources.push(event.option.value);
    const index = this.resources.indexOf(event.option.value);
    if (index !== -1) {
      this.resources.splice(index, 1);
    }
  }

  onSave() {
    const postData: IRooms = {
      name: this.addEditRoomsFormGroup.get('name').value,
      seating_capacity: parseInt(
        this.addEditRoomsFormGroup.get('seatingCapacity').value
      ),
      status: ROOMS_STATUS.ACTIVATED,
      description: this.addEditRoomsFormGroup.get('description').value,
      available_resources: this.filteredResources,
    };

    this.roomsService.createRoom(postData).subscribe({
      next: (response) => {
        this.snackbar.success(
          SUCCESS_TYPES.ADDED,
          this.translate.instant('rooms.add-edit.form.room')
        );

        this.router.navigate(['/app/rooms/'], {
          queryParams: {
            is_search: true,
            name: response.data.name,
          },
        });
      },
      error: () => {
        this.snackbar.error(
          this.translate.instant('rooms.add-edit.form.on-create-error')
        );
      },
    });
  }

  onUpdate() {
    const postData: IRooms = {
      name: this.addEditRoomsFormGroup.get('name').value,
      seating_capacity: parseInt(
        this.addEditRoomsFormGroup.get('seatingCapacity').value
      ),
      status: ROOMS_STATUS.ACTIVATED,
      description: this.addEditRoomsFormGroup.get('description').value,
      available_resources: this.filteredResources,
    };

    this.roomsService.updateRoomById(postData, this.roomId).subscribe({
      next: (response) => {
        this.snackbar.success(
          SUCCESS_TYPES.UPDATED,
          this.translate.instant('rooms.add-edit.form.room')
        );

        this.router.navigate(['/app/rooms/'], {
          queryParams: {
            is_search: true,
            name: response.data.name,
          },
        });
      },
      error: () => {
        this.snackbar.error(
          this.translate.instant('rooms.add-edit.form.on-update.error')
        );
      },
    });
  }
}
